import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import config from '../config';
import { useAdminApi } from './hooks/useAdminApi';

type SiteContentType = 'Blogs' | 'Faq' | 'Page' | 'HowItWorks';

type SiteContentItem = {
  id: number;
  organizationId: number;
  contentType: SiteContentType;
  slug: string;
  title: string;
  summary?: string | null;
  htmlContent: string;
  tags?: string | null;
  mainImageUrl?: string | null;
  mainImageAlt?: string | null;
  categoryId?: number | null;
  isPublished: boolean;
  publishedAt?: string | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  createdAt: string;
  updatedAt: string;
};

type FormState = {
  contentType: SiteContentType;
  slug: string;
  title: string;
  summary: string;
  htmlContent: string;
  tags: string;
  mainImageUrl: string;
  mainImageAlt: string;
  categoryId: string;
  isPublished: boolean;
  publishedAt: string;
  seoTitle: string;
  seoDescription: string;
};

const emptyForm: FormState = {
  contentType: 'Blogs',
  slug: '',
  title: '',
  summary: '',
  htmlContent: '',
  tags: '',
  mainImageUrl: '',
  mainImageAlt: '',
  categoryId: '',
  isPublished: true,
  publishedAt: '',
  seoTitle: '',
  seoDescription: '',
};

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const ManageSiteContent: React.FC = () => {
  const { hasActiveOrganization, getManagementToken, getOrganizationHeaders } = useAdminApi();
  const [items, setItems] = useState<SiteContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | SiteContentType>('all');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);

  const API_BASE = `${config.apiBaseUrl}/api`;

  const filteredItems = useMemo(() => {
    const q = searchText.trim().toLowerCase();

    return items.filter((item) => {
      const typeOk = typeFilter === 'all' || item.contentType === typeFilter;
      if (!typeOk) {
        return false;
      }

      if (!q) {
        return true;
      }

      return (
        item.title.toLowerCase().includes(q) ||
        item.slug.toLowerCase().includes(q) ||
        (item.summary || '').toLowerCase().includes(q) ||
        (item.tags || '').toLowerCase().includes(q)
      );
    });
  }, [items, searchText, typeFilter]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setMessage(null);

      if (!hasActiveOrganization) {
        setItems([]);
        return;
      }

      const token = await getManagementToken();
      const response = await axios.get<SiteContentItem[]>(`${API_BASE}/sitecontent/admin`, {
        headers: getOrganizationHeaders(token),
      });

      setItems(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load site content');
    } finally {
      setLoading(false);
    }
  }, [API_BASE, getManagementToken, getOrganizationHeaders, hasActiveOrganization]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const startAdd = () => {
    setIsAdding(true);
    setEditingId(null);
    setForm(emptyForm);
    setError(null);
    setMessage(null);
  };

  const startEdit = (item: SiteContentItem) => {
    setIsAdding(false);
    setEditingId(item.id);
    setForm({
      contentType: item.contentType,
      slug: item.slug,
      title: item.title,
      summary: item.summary || '',
      htmlContent: item.htmlContent || '',
      tags: item.tags || '',
      mainImageUrl: item.mainImageUrl || '',
      mainImageAlt: item.mainImageAlt || '',
      categoryId: item.categoryId ? String(item.categoryId) : '',
      isPublished: item.isPublished,
      publishedAt: item.publishedAt ? item.publishedAt.slice(0, 16) : '',
      seoTitle: item.seoTitle || '',
      seoDescription: item.seoDescription || '',
    });
    setError(null);
    setMessage(null);
  };

  const cancelEdit = () => {
    setIsAdding(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const autoFillSlug = () => {
    if (form.slug.trim()) {
      return;
    }

    const generated = slugify(form.title);
    setForm((prev) => ({ ...prev, slug: generated }));
  };

  const saveItem = async () => {
    if (!hasActiveOrganization) {
      setError('Please select an organization before managing site content.');
      return;
    }

    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }

    if (!form.htmlContent.trim()) {
      setError('HTML content is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setMessage(null);

      const token = await getManagementToken();
      const payload = {
        contentType: form.contentType,
        slug: form.slug.trim() || slugify(form.title),
        title: form.title.trim(),
        summary: form.summary.trim() || null,
        htmlContent: form.htmlContent,
        tags: form.tags.trim() || null,
        mainImageUrl: form.mainImageUrl.trim() || null,
        mainImageAlt: form.mainImageAlt.trim() || null,
        categoryId: form.categoryId.trim() ? Number(form.categoryId) : null,
        isPublished: form.isPublished,
        publishedAt: form.publishedAt ? new Date(form.publishedAt).toISOString() : null,
        seoTitle: form.seoTitle.trim() || null,
        seoDescription: form.seoDescription.trim() || null,
      };

      if (isAdding) {
        await axios.post(`${API_BASE}/sitecontent`, payload, {
          headers: getOrganizationHeaders(token),
        });
      } else if (editingId) {
        await axios.put(`${API_BASE}/sitecontent/${editingId}`, payload, {
          headers: getOrganizationHeaders(token),
        });
      }

      await loadData();
      cancelEdit();
      setMessage('Site content saved successfully.');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save site content');
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (id: number) => {
    if (!window.confirm('Delete this content item?')) {
      return;
    }

    try {
      setError(null);
      setMessage(null);
      const token = await getManagementToken();

      await axios.delete(`${API_BASE}/sitecontent/${id}`, {
        headers: getOrganizationHeaders(token),
      });

      setItems((prev) => prev.filter((item) => item.id !== id));
      if (editingId === id) {
        cancelEdit();
      }
      setMessage('Site content deleted successfully.');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete site content');
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setUploadingImage(true);
      setError(null);
      setMessage(null);

      const token = await getManagementToken();
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('assetType', 'site-content');
      uploadFormData.append('entityType', 'site-content');
      if (editingId) {
        uploadFormData.append('entityId', String(editingId));
      }

      const response = await fetch(`${API_BASE}/assets/upload`, {
        method: 'POST',
        headers: getOrganizationHeaders(token),
        body: uploadFormData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.message || 'Failed to upload image');
      }

      const data = await response.json();
      setForm((prev) => ({ ...prev, mainImageUrl: data.url || '' }));
      setMessage('Image uploaded successfully. Blob URL added to MainImageUrl.');
    } catch (uploadError: any) {
      setError(uploadError?.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  return (
    <div className="bg-gradient-to-br from-app-page-from to-app-page-to" style={{ width: '100%', minHeight: '100vh' }}>
      <div className="app-breadcrumb-bar">
        <div className="app-breadcrumb-row">
          <button onClick={() => window.location.assign('/admin')} className="app-breadcrumb-link">Admin</button>
          <span className="app-breadcrumb-sep">›</span>
          <span className="app-breadcrumb-current">Manage Site Content</span>
        </div>
      </div>

      <div className="p-3 md:p-6">
        {!hasActiveOrganization && (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 text-left">
            Select an organization first to manage site content.
          </div>
        )}

        {error && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 text-left">{error}</div>
        )}

        {message && (
          <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 text-left">{message}</div>
        )}

        <div className="rounded-lg bg-white shadow-md overflow-hidden">
          <div className="p-4 md:p-5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-t-lg">
            <div className="flex items-center justify-between gap-3">
              <div className="text-left">
                <h1 className="text-xl md:text-2xl font-bold">Manage Site Content</h1>
                <p className="text-xs md:text-sm text-white/80">Blogs, FAQ and content pages with image metadata</p>
              </div>
              <button
                onClick={startAdd}
                className="rounded-lg bg-white/20 px-4 py-2 text-sm font-semibold hover:bg-white/30"
                disabled={!hasActiveOrganization}
              >
                + Add Content
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr]">
            <div className="border-r border-slate-100 p-4">
              <div className="grid grid-cols-1 gap-2">
                <input
                  type="text"
                  placeholder="Search by title, slug, tags"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value as 'all' | SiteContentType)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="all">All types</option>
                  <option value="Blogs">Blogs</option>
                  <option value="Faq">FAQ</option>
                  <option value="Page">Page</option>
                  <option value="HowItWorks">How It Works</option>
                </select>
              </div>

              <div className="mt-3 max-h-[70vh] overflow-auto space-y-2">
                {loading ? (
                  <div className="text-sm text-slate-600">Loading...</div>
                ) : filteredItems.length === 0 ? (
                  <div className="text-sm text-slate-600">No content items found.</div>
                ) : (
                  filteredItems.map((item) => {
                    const selected = editingId === item.id;
                    return (
                      <div
                        key={item.id}
                        className={`rounded-lg border p-3 text-left ${selected ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-slate-50'}`}
                      >
                        <button className="w-full text-left" onClick={() => startEdit(item)}>
                          <p className="text-sm font-semibold text-slate-900">{item.title}</p>
                          <p className="mt-1 text-xs text-slate-500">{item.contentType} • /{item.slug}</p>
                          <p className="mt-1 text-[11px] text-slate-500">{item.isPublished ? 'Published' : 'Draft'}</p>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            void deleteItem(item.id);
                          }}
                          className="mt-2 rounded bg-red-600 px-2 py-1 text-xs font-semibold text-white hover:bg-red-700"
                        >
                          Delete
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="p-4 md:p-5 text-left">
              {(isAdding || editingId) ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600">Content Type</label>
                      <select
                        value={form.contentType}
                        onChange={(e) => setForm((prev) => ({ ...prev, contentType: e.target.value as SiteContentType }))}
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                      >
                        <option value="Blogs">Blogs</option>
                        <option value="Faq">FAQ</option>
                        <option value="Page">Page</option>
                        <option value="HowItWorks">How It Works</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600">Slug</label>
                      <input
                        value={form.slug}
                        onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
                        onBlur={autoFillSlug}
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        placeholder="auto-generated-from-title"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600">Title</label>
                    <input
                      value={form.title}
                      onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600">Summary / Header</label>
                    <textarea
                      value={form.summary}
                      onChange={(e) => setForm((prev) => ({ ...prev, summary: e.target.value }))}
                      rows={2}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600">HTML Content</label>
                    <textarea
                      value={form.htmlContent}
                      onChange={(e) => setForm((prev) => ({ ...prev, htmlContent: e.target.value }))}
                      rows={12}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600">MainImageUrl</label>
                      <input
                        value={form.mainImageUrl}
                        onChange={(e) => setForm((prev) => ({ ...prev, mainImageUrl: e.target.value }))}
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        placeholder="https://..."
                      />
                      <div className="mt-2">
                        <label className="inline-flex items-center rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white cursor-pointer hover:bg-blue-700">
                          {uploadingImage ? 'Uploading...' : 'Upload Image'}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(event) => {
                              void handleImageUpload(event);
                            }}
                            disabled={uploadingImage}
                          />
                        </label>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600">MainImageAlt</label>
                      <input
                        value={form.mainImageAlt}
                        onChange={(e) => setForm((prev) => ({ ...prev, mainImageAlt: e.target.value }))}
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        placeholder="Descriptive alt text"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600">Tags (comma separated)</label>
                      <input
                        value={form.tags}
                        onChange={(e) => setForm((prev) => ({ ...prev, tags: e.target.value }))}
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600">CategoryId (optional)</label>
                      <input
                        value={form.categoryId}
                        onChange={(e) => setForm((prev) => ({ ...prev, categoryId: e.target.value }))}
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600">SEO Title</label>
                      <input
                        value={form.seoTitle}
                        onChange={(e) => setForm((prev) => ({ ...prev, seoTitle: e.target.value }))}
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600">SEO Description</label>
                      <input
                        value={form.seoDescription}
                        onChange={(e) => setForm((prev) => ({ ...prev, seoDescription: e.target.value }))}
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={form.isPublished}
                        onChange={(e) => setForm((prev) => ({ ...prev, isPublished: e.target.checked }))}
                      />
                      Published
                    </label>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600">Published At (optional)</label>
                      <input
                        type="datetime-local"
                        value={form.publishedAt}
                        onChange={(e) => setForm((prev) => ({ ...prev, publishedAt: e.target.value }))}
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                      />
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => {
                        void saveItem();
                      }}
                      disabled={saving}
                      className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-70"
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-600">Select an item from the list or add a new content item.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageSiteContent;
